import { ArquivoService } from '../../service';
import {
  Arquivo,
  ArquivoMultipartUrl,
  ArquivoParteUpload,
  ArquivoUrl
} from '../../type';
import { FileType } from '../../type/enum';
import {
  ProgressAxiosRequestConfig,
  TransProgressKind,
  failTransProgressFile,
  transientRequest,
  useTransProgress
} from '../transference-progress';
import { ArquivoEntityWithTemp } from './ArquivoComponent';

const MULTIPART_THRESHOLD_BYTES = 1024 * 1024 * 1024; // 1GB
const MULTIPART_PART_SIZE_BYTES = 500 * 1024 * 1024; // 500MB
const MULTIPART_MIN_LAST_PART_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

export const getUnaccentFileName = (fileName: string) => {
  return fileName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const isHtmlFile = ({ nome }: Pick<Arquivo, 'mimeType' | 'nome'>) => {
  return !!nome?.toLowerCase().endsWith('.html');
};

const arquivoUrlToArquivoEntity = (
  arquivoUrl: Omit<ArquivoUrl, 'dataCriacao' | 'urlAssinada'> & {
    dataCriacao?: string;
  },
  file: File,
  arquivoEntityId?: string,
  patches?: any
): ArquivoEntityWithTemp => {
  const entity = {
    domainEntity: arquivoEntityId ? { id: arquivoEntityId } : undefined,
    arquivo: {
      nome: getUnaccentFileName(file.name),
      mimeType: file.type,
      dataCriacao: arquivoUrl.dataCriacao || new Date().toISOString(),
      fullPath: arquivoUrl.fullPath
    },
    ...(!arquivoEntityId && {
      temporario: true,
      localFile: file
    })
  };

  updateEntityWithPatches(entity, patches);

  return entity;
};

export const updateEntityWithPatches = (entity, patches) => {
  if (!patches) {
    return;
  }
  Object.keys(patches).forEach(key => {
    if (
      entity[key] &&
      patches[key] &&
      typeof entity[key] === 'object' &&
      typeof patches[key] === 'object'
    ) {
      updateEntityWithPatches(entity[key], patches[key]);
      return;
    }

    entity[key] = patches[key];
  });
};

export const getFileName = (entity: any, file: File) => {
  return (
    entity?.arquivo?.nome ||
    entity?.nome ||
    entity?.fileName ||
    entity?.name ||
    file.name
  );
};

export const sliceFileParts = (file: File): Blob[] => {
  const partes: Blob[] = [];
  for (let start = 0; start < file.size; start += MULTIPART_PART_SIZE_BYTES) {
    partes.push(
      file.slice(start, Math.min(start + MULTIPART_PART_SIZE_BYTES, file.size))
    );
  }
  const ultimaParte = partes[partes.length - 1];
  if (
    ultimaParte.size < MULTIPART_MIN_LAST_PART_SIZE_BYTES &&
    partes.length > 1
  ) {
    const penultimatePart = partes[partes.length - 2];
    const newLastPart = new Blob([penultimatePart, ultimaParte], {
      type: ultimaParte.type
    });
    partes.splice(partes.length - 2, 2, newLastPart);
  }
  return partes;
};

export const uploadArquivoMultipart = async (
  service: Pick<
    ArquivoService,
    | 'gerarUrlsParaMultipartUpload'
    | 'enviarParteArquivoParaServidor'
    | 'finalizarMultipartUpload'
    | 'abortarMultipartUpload'
  >,
  filePath: number,
  file: File,
  fileName: string,
  configs: ProgressAxiosRequestConfig,
  contentType: string = file.type || FileType.BIN
): Promise<ArquivoUrl> => {
  const partesBlob = sliceFileParts(file);
  const onUploadProgress = configs.onUploadProgress;

  const { data: urls } = await service.gerarUrlsParaMultipartUpload(
    filePath,
    fileName,
    contentType,
    partesBlob.length
  );

  const urlsPorNumeroParte: ArquivoMultipartUrl[] = [...urls].sort(
    (a, b) => a.numeroParte - b.numeroParte
  );

  const { fullPath, uploadId } = urlsPorNumeroParte[0];

  try {
    let bytesEnviados = 0;
    const partesEnviadas: ArquivoParteUpload[] = [];

    for (let i = 0; i < partesBlob.length; i++) {
      const parte = partesBlob[i];
      const urlParte = urlsPorNumeroParte[i];

      const response = await service.enviarParteArquivoParaServidor(
        parte,
        urlParte.urlAssinada,
        transientRequest(
          {
            ...configs,
            headers: { 'Content-Type': contentType },
            onUploadProgress: progressEvent => {
              onUploadProgress?.({
                ...progressEvent,
                loaded: bytesEnviados + progressEvent.loaded,
                total: file.size
              });
            }
          },
          true,
          false
        )
      );

      const etag = response.headers.etag || response.headers.ETag;
      if (!etag) {
        const erro = new Error(
          `Não foi possível ler o ETag da parte ${urlParte.numeroParte} do upload. O bucket S3 provavelmente não está expondo o header ETag via CORS (Access-Control-Expose-Headers).`
        );
        failTransProgressFile(configs.fileId, erro);
        throw erro;
      }

      bytesEnviados += parte.size;
      partesEnviadas.push({
        numeroParte: urlParte.numeroParte,
        etag
      });
    }

    const { data: arquivoUrl } = await service.finalizarMultipartUpload(
      fullPath,
      uploadId,
      partesEnviadas,
      transientRequest(configs, false, false)
    );

    return arquivoUrl;
  } catch (erro) {
    failTransProgressFile(configs.fileId, erro);
    abortarMultipartUpload(service, fullPath, fileName, uploadId);
    throw erro;
  }
};

/**
 * Cancela o upload em multipart pra não deixar as partes já enviadas ocupando espaço no
 * storage. É best-effort: uma falha aqui não pode esconder o erro original do upload.
 */
const abortarMultipartUpload = async (
  service: Pick<ArquivoService, 'abortarMultipartUpload'>,
  fullPath: string,
  fileName: string,
  uploadId: string
) =>
  service
    .abortarMultipartUpload(fullPath, fileName, uploadId)
    .catch(erroAoAbortar =>
      // eslint-disable-next-line no-console
      console.warn(
        `Falha ao cancelar o upload em multipart do arquivo ${fileName} (uploadId: ${uploadId}). As partes já enviadas podem seguir ocupando espaço no storage.`,
        erroAoAbortar
      )
    );

const uploadArquivoSingle = async (
  service: ArquivoService,
  filePath: number,
  file: File,
  fileName: string,
  configs: ProgressAxiosRequestConfig
): Promise<ArquivoUrl> => {
  const { data: arquivoUrl } = await service.gerarUrlParaUpload(
    filePath,
    fileName,
    file.type
  );

  await service.enviarArquivoParaServidor(
    file,
    arquivoUrl.urlAssinada,
    transientRequest(
      {
        ...configs,
        headers: { 'Content-Type': file.type || FileType.BIN }
      },
      true,
      false
    )
  );

  return arquivoUrl;
};

export const shouldUseMultipartUpload = (file: File): boolean => {
  return file.size > MULTIPART_THRESHOLD_BYTES;
};

const uploadArquivo = (
  service: ArquivoService,
  filePath: number,
  file: File,
  fileName: string,
  configs: ProgressAxiosRequestConfig
): Promise<ArquivoUrl> =>
  shouldUseMultipartUpload(file)
    ? uploadArquivoMultipart(service, filePath, file, fileName, configs)
    : uploadArquivoSingle(service, filePath, file, fileName, configs);

export const ArquivoUtils = (service: ArquivoService, filePath: number) => ({
  arquivoUrlToArquivoEntity,
  getUnaccentFileName,

  downloadArquivo: async ({
    url,
    nome,
    mimeType
  }: {
    url: string;
    nome?: string;
    mimeType?: string;
  }) => {
    return service
      .receberArquivoDoServidor(
        url,
        useTransProgress(nome || 'Arquivo sem nome', TransProgressKind.DOWNLOAD)
      )
      .then(({ data }) => {
        return new Blob([data], {
          type: mimeType
        });
      });
  },
  uploadFile: ({ file, fileName }: { file: File; fileName: string }) =>
    uploadArquivo(
      service,
      filePath,
      file,
      fileName,
      useTransProgress(fileName, TransProgressKind.UPLOAD)
    ),
  saveFileToEntity: ({
    file,
    entityId,
    fileName,
    patches
  }: {
    file: File;
    fileName: string;
    entityId: string;
    patches?: any;
  }) => {
    const configs = useTransProgress(fileName, TransProgressKind.UPLOAD);

    return uploadArquivo(service, filePath, file, fileName, configs).then(
      arquivoUrl =>
        service.salvarArquivoEntity(
          entityId,
          arquivoUrlToArquivoEntity(arquivoUrl, file, entityId, patches),
          configs
        )
    );
  }
});
